export const en = {
  nav: {
    features: 'Features',
    howItWorks: 'How It Works',
    pricing: 'Pricing',
    community: 'Community',
    startFree: 'Start Free',
  },
  hero: {
    title: 'Your Longevity Co-pilot',
    titleHighlight: '',
    titleEnd: '',
    description: 'We turn your data and goals into a plan to live longer.',
    tryFree: 'Try 7 days',
    watchDemo: 'Watch Demo',
    price: 'per month',
    automatic: 'automatically',
    personalPlan: 'personal plan',
  },
  beforeAfter: {
    badge: 'Expected Results',
    title: 'Before and After',
    description: 'See how your life can change in the first 6 months of using Juvenis',
    energy: 'Energy',
    sleep: 'Sleep',
    longevity: 'Longevity',
    before: 'BEFORE',
    after: 'AFTER',
    beforeLabel: 'Before',
    afterLabel: 'After',
    resultsNote: 'Results are based on scientific research and health biomarker analysis',
    resultsNoteSmall: 'Individual results may vary depending on baseline data and adherence to recommendations',
    energyBefore: {
      value: '45%',
      label: 'Constant Fatigue',
      description: 'Wake up exhausted, coffee doesn\'t help',
    },
    energyAfter: {
      value: '92%',
      label: 'Energy All Day',
      description: 'Energy from morning to evening without stimulants',
    },
    sleepBefore: {
      value: '5.2h',
      label: 'Poor Sleep',
      description: 'Frequent awakenings, not getting enough rest',
    },
    sleepAfter: {
      value: '8.1h',
      label: 'Deep Sleep',
      description: 'Wake up rested and refreshed',
    },
    longevityBefore: {
      value: '68 years',
      label: 'Biological Age',
      description: 'Body aging faster than the calendar',
    },
    longevityAfter: {
      value: '52 years',
      label: 'Biological Age',
      description: '16 years younger in 6 months',
    },
  },
  features: {
    title: 'Daily Plan',
    subtitle: 'Every day you get 3 actions that actually move your trajectory.',
    cardTitle: 'What you see in the app',
    sections: {
      today: 'TODAY',
      why: 'WHY',
      done: 'TASKS',
      adapt: 'ADAPT',
    },
    today: {
      sleep: 'Sleep',
      food: 'Food',
      movement: 'Movement',
    },
    why: {
      sleep: 'Yesterday sleep was shorter by 1.5h — reducing load, prioritizing recovery',
      food: 'Low protein yesterday — increasing portions, adding protein',
      movement: 'Sedentary day — adding light activity, walks',
    },
    done: {
      sleep: {
        busy: {
          task1: 'Get sunlight before 10:00 (15-20 minutes)',
          task2: 'Take 400mg magnesium 1 hour before bed',
          task3: 'Go to bed before 23:00, turn off screens 30 min before',
        },
        travel: {
          task1: 'Morning sunlight (10-15 minutes)',
          task2: 'Dinner before 19:00, light food',
          task3: 'Go to bed before 22:30, darken the room',
        },
      },
      food: {
        busy: {
          task1: '30g protein for breakfast (eggs/cottage cheese/protein)',
          task2: '150g vegetables in every meal',
          task3: 'Drink 2.5L water throughout the day',
        },
        travel: {
          task1: 'Protein in every meal (minimum 20g)',
          task2: 'Light snacks every 3-4 hours',
          task3: 'Drink 2L water, avoid dehydration',
        },
      },
      movement: {
        busy: {
          task1: '20-minute walk in fresh air',
          task2: '10 minutes of stretching or yoga',
          task3: 'Walk at least 5000 steps during the day',
        },
        travel: {
          task1: 'Breathing exercises 5 minutes (4-7-8)',
          task2: 'Go to bed 30 minutes earlier than usual',
          task3: 'Light walking 10-15 minutes after meals',
        },
      },
    },
    adapt: {
      busy: 'Busy day',
      travel: 'Travel mode',
      note: 'Plan simplifies automatically',
    },
  },
  analytics: {
    title: 'Beautiful Analytics',
    description: 'Track your progress with intuitive graphs and dashboards',
    currentMetrics: 'Current Metrics',
    weeklyDynamics: 'Weekly Dynamics',
    improvementTrends: 'Improvement Trends',
    energy: 'Energy',
    sleepQuality: 'Sleep Quality',
    activity: 'Physical Activity',
    recovery: 'Recovery',
    sleep: 'Sleep',
    averageEnergy: 'Average Energy',
    sleepQualityLabel: 'Sleep Quality',
    recoveryLabel: 'Recovery',
    period: 'over 3 months',
    days: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
  },
  howItWorks: {
    title: 'How it works',
    description: 'Three simple steps to better health',
    step1: {
      title: 'Connect Devices',
      description: 'Sync Apple Health, Oura Ring, WHOOP, or other trackers. The app will automatically start collecting data about your sleep, activity, and health.',
    },
    step2: {
      title: 'Get Your Daily Plan',
      description: 'AI analyzes your data and offers 3 simple tasks every morning. No overload - only what\'s truly important for your health.',
    },
    step3: {
      title: 'Track Progress',
      description: 'View beautiful graphs, study materials, connect with the community. Observe how your health improves day by day.',
    },
  },
  pricing: {
    title: 'Simple Pricing',
    description: 'Cheaper than a cup of coffee per day',
    popular: 'POPULAR',
    features: [
      'Personal action plan every day',
      'Integration with Apple Health / Oura / WHOOP',
      'Knowledge base and educational content',
      'Analytics and dashboards',
      'Automatic test interpretation',
      'Access to private community',
      '100% automated service',
    ],
    startFree: 'Start 7 days free',
    note: 'First week free, then $99/month. Cancel anytime.',
  },
  community: {
    title: 'Personalized Approach to Longevity',
    description: 'Personal consultations and recommendations for extending life and reducing biological age based on analysis of your unique data',
    biomarker: {
      title: 'Biomarker Analysis',
      description: 'Deep analysis of your data to determine biological age',
    },
    recommendations: {
      title: 'Personal Recommendations',
      description: 'Individual advice based on your unique metrics',
    },
    knowledge: {
      title: 'Knowledge Base',
      description: 'Access to exclusive materials, research, and guides',
    },
    plan: {
      title: 'Longevity Plan',
      description: 'Personalized plan for extending life and improving health',
    },
    consultations: {
      title: 'Expert Consultations',
      description: 'Get personal recommendations from certified specialists for the most effective path to longevity',
    },
    trainer: {
      title: 'Trainer',
      description: 'Personal training programs adapted to your fitness level and longevity goals',
    },
    nutritionist: {
      title: 'Nutritionist',
      description: 'Individual nutrition plan considering your biomarkers, food preferences, and health goals',
    },
    doctor: {
      title: 'Doctor',
      description: 'Medical consultations and test interpretation for a comprehensive approach to life extension',
    },
    personalPlanTitle: 'What\'s Included in Personal Plan',
    benefits: [
      'Personal recommendations based on your data',
      'AI biomarker analysis for life extension',
      'Individual plan to reduce biological age',
      'Access to scientific research on longevity',
    ],
    startJourney: 'Start Your Longevity Journey',
    joinDescription: 'Join the waitlist and get early access to personal recommendations',
    join: 'Join',
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Everything you need to know about Juvenis',
    questions: [
      {
        question: 'Is this a replacement for a doctor?',
        answer: 'No. This is a product for lifestyle/longevity routine and decision-making, not for diagnosis and treatment.',
      },
      {
        question: 'Do I need wearables (Oura/WHOOP)?',
        answer: 'No. You can start with check-ins and basic data, but with wearables it\'s more accurate.',
      },
      {
        question: 'How do you calculate Bio Age?',
        answer: 'This is an index-reference that shows dynamics, not a medical diagnosis.',
      },
      {
        question: 'Will there be a personal manager?',
        answer: 'In this plan — there is no dedicated manager. Everything is automated for maximum efficiency.',
      },
      {
        question: 'How quickly will I see results?',
        answer: 'Usually the first improvements — in routine and clarity — already in the first week.',
      },
      {
        question: 'Can I cancel?',
        answer: 'Yes, in 1 click. No questions asked.',
      },
      {
        question: 'What about privacy?',
        answer: 'We don\'t sell data and use it only for the product to work.',
      },
    ],
  },
  cta: {
    title: 'Start Your Longevity Journey Today',
    description: 'Start your longevity journey today. First week free.',
    tryFree: 'Try Free',
    note: 'No credit card required. Cancel anytime.',
  },
  footer: {
    tagline: 'Your Longevity Co-pilot',
    product: 'Product',
    company: 'Company',
    support: 'Support',
    about: 'About',
    blog: 'Blog',
    career: 'Career',
    help: 'Help',
    contacts: 'Contacts',
    privacy: 'Privacy Policy',
    termsOfUse: 'Terms of Use',
    copyright: '© 2026 Juvenis. All rights reserved.',
  },
  terms: {
    title: 'Terms of Use',
    lastUpdated: 'Last Updated: February 3, 2026',
    welcome: 'Welcome to Juvenis! These Terms of Use ("Terms") govern your access to and use of the juvenis.app website, our platform, and related services (collectively, the "Service"), provided by Juvenis ("Company," "we," "us," or "our").',
    agree: 'By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to any part of the Terms, you may not use the Service.',
    section1Title: '1. Important Notice: The Service Does Not Provide Medical Advice',
    section1Warning: 'OUR SERVICE IS NOT A SUBSTITUTE FOR PROFESSIONAL MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT. THE INFORMATION, RECOMMENDATIONS, AND PLANS GENERATED BY OUR PLATFORM BASED ON YOUR DATA ARE PROVIDED FOR INFORMATIONAL AND EDUCATIONAL PURPOSES ONLY.',
    section1Acknowledge: 'You acknowledge and agree that:',
    section1List1: 'You must always consult with a qualified physician or other healthcare provider regarding any questions you have about a medical condition or your health.',
    section1List2: 'Never disregard professional medical advice or delay in seeking it because of something you have read or received through our Service.',
    section1List3: 'Any decisions you make based on information from Juvenis are at your own sole risk. We are not liable or responsible for any actions or inaction on your part.',
    section1List4: 'If you think you may have a medical emergency, call your doctor or emergency services immediately.',
    section2Title: '2. Description of the Service',
    section2Text: 'Juvenis is a platform that uses data provided by you (including blood test results, genetic data, and lifestyle information) to generate personalized recommendations and plans aimed at optimizing your health and longevity. The Service may also facilitate connections with independent physicians and coaches ("Specialists").',
    section3Title: '3. User Responsibilities',
    section3Text: 'You are solely responsible for:',
    section3List1: 'The accuracy, completeness, and truthfulness of all data you provide to the Service.',
    section3List2: 'Ensuring you have obtained all data lawfully and have the right to share it with us.',
    section3List3: 'Maintaining the confidentiality of your account information.',
    section3List4: 'Independently evaluating and acting upon the recommendations from the Service, preferably after consulting with your primary care physician.',
    section4Title: '4. Connection with Third-Party Specialists',
    section4Text: 'The platform may help you connect with independent Specialists. You understand that:',
    section4List1: 'Juvenis acts solely as a technology platform to facilitate this connection. We do not employ or control these Specialists.',
    section4List2: 'We are not responsible for the quality, accuracy, timeliness, or legality of the advice and services provided by these Specialists.',
    section4List3: 'Any relationship you establish with a Specialist is a direct relationship between you and them. All claims related to their services must be directed to the Specialist.',
    section5Title: '5. Limitation of Liability',
    section5Warning: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY, ITS AFFILIATES, DIRECTORS, AND EMPLOYEES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR HEALTH, WHETHER INCURRED DIRECTLY OR INDIRECTLY, RESULTING FROM:',
    section5List1: 'YOUR USE OF OR INABILITY TO USE THE SERVICE;',
    section5List2: 'ANY RECOMMENDATIONS OR INFORMATION OBTAINED THROUGH THE SERVICE;',
    section5List3: 'THE CONDUCT OR ADVICE OF ANY THIRD-PARTY SPECIALISTS;',
    section5List4: 'UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR DATA.',
    section5Text: 'OUR AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS RELATING TO THE SERVICE SHALL BE LIMITED TO THE AMOUNT PAID BY YOU, IF ANY, FOR USING THE SERVICE DURING THE 12-MONTH PERIOD PRECEDING THE CLAIM.',
    section6Title: '6. Disclaimer of Warranties',
    section6Text: 'THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT THE RESULTS OBTAINED FROM ITS USE WILL BE ACCURATE, RELIABLE, OR MEET YOUR EXPECTATIONS.',
    section7Title: '7. Intellectual Property',
    section7Text: 'All content, trademarks, logos, and software associated with the Service are the property of the Company or its licensors and are protected by intellectual property laws.',
    section8Title: '8. Modification and Termination',
    section8Text: 'We reserve the right to modify these Terms at any time. We may also suspend or terminate your access to the Service for any reason, without prior notice.',
    section9Title: '9. Governing Law',
    section9Text: 'These Terms shall be governed by and construed in accordance with the laws of UAE, without regard to its conflict of law principles.',
    section10Title: '10. Contact Information',
    section10Text: 'If you have any questions about these Terms, please contact us at contact@juvenis.app',
    backToHome: 'Back to Home',
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last Updated: February 3, 2026',
    intro: 'Juvenis ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.',
    section1Title: '1. Information We Collect',
    section1Text: 'We may collect the following categories of information:',
    section1List1Label: 'Personal Identifying Information:',
    section1List1Text: 'Your name, email address, date of birth, and other contact details you provide upon registration.',
    section1List2Label: 'Health and Genetic Information:',
    section1List2Text: 'Blood test results, genetic data, information about diagnoses, medications, family history, and other health-related data you upload or provide.',
    section1List3Label: 'Lifestyle Information:',
    section1List3Text: 'Data about your diet, physical activity, sleep patterns, stress levels, and other habits.',
    section1List4Label: 'Technical Information:',
    section1List4Text: 'IP address, browser type, operating system, cookies, and usage data related to our website and platform.',
    section2Title: '2. How We Use Your Information',
    section2Text: 'We use the information we collect to:',
    section2List1Label: 'Provide and Personalize the Service:',
    section2List1Text: 'To analyze your data and generate your personalized longevity plan and recommendations.',
    section2List2Label: 'Communicate With You:',
    section2List2Text: 'To send you notifications, updates, and respond to your inquiries.',
    section2List3Label: 'Improve Our Service:',
    section2List3Text: 'We may use anonymized and aggregated data for research, trend analysis, and to improve our algorithms and offerings.',
    section2List4Label: 'Connect You with Specialists:',
    section2List4Text: 'With your explicit consent, we may share parts of your information with a Specialist you choose to consult.',
    section2List5Label: 'Legal Purposes:',
    section2List5Text: 'To comply with legal obligations and protect our rights.',
    section3Title: '3. How We Share Your Information',
    section3Text: 'We do not sell or rent your personal health or genetic information to third parties without your explicit consent, except in the following cases:',
    section3List1Label: 'Service Providers:',
    section3List1Text: 'We may share information with trusted third-party vendors who assist us in operating our business (e.g., cloud storage providers), provided they adhere to strict confidentiality obligations.',
    section3List2Label: 'Specialists:',
    section3List2Text: 'With your direct permission, we will share necessary information with a Specialist you choose to connect with through our platform.',
    section3List3Label: 'Anonymized Data:',
    section3List3Text: 'We may share anonymized and aggregated data (which cannot be used to identify you) with research partners.',
    section3List4Label: 'Legal Requirements:',
    section3List4Text: 'We may disclose your information if required by law, court order, or in response to a valid request from a governmental authority.',
    section4Title: '4. Data Security',
    section4Text: 'We implement commercially reasonable technical and organizational measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure. Therefore, we cannot guarantee its absolute security.',
    section5Title: '5. Your Rights and Choices',
    section5Text: 'Depending on your jurisdiction, you may have the following rights:',
    section5List1Label: 'Right to Access:',
    section5List1Text: 'You can request a copy of the information we hold about you.',
    section5List2Label: 'Right to Rectification:',
    section5List2Text: 'You can request to correct inaccurate or incomplete information.',
    section5List3Label: 'Right to Erasure:',
    section5List3Text: 'You can request the deletion of your account and associated information.',
    section6Title: '6. Children\'s Privacy',
    section6Text: 'Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children.',
    section7Title: '7. Changes to This Policy',
    section7Text: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Policy on this page.',
    section8Title: '8. Contact Information',
    section8Text: 'If you have any questions or concerns about our Privacy Policy, please contact us at contact@juvenis.app',
    backToHome: 'Back to Home',
  },
  waitlist: {
    badge: 'Launching Soon',
    title: 'Your path to',
    titleHighlight: 'longevity',
    titleEnd: 'starts here',
    description: 'Be among the first to get access to',
    descriptionHighlight: 'personal longevity plan',
    descriptionEnd: ', based on scientific data and analysis of your biomarkers.',
    emailPlaceholder: 'your@email.com',
    join: 'Join',
    privacy: 'We don\'t share your data with third parties. Only important updates about the launch.',
    success: 'You\'re on the list!',
    successDescription: 'We\'ve sent you a confirmation email. We\'ll contact you soon when the platform is ready!',
    back: '← Back to Home',
    invalidEmail: 'Please enter a valid email address',
  },
}
